import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Mail } from "lucide-mui";
import { useNavigate } from "react-router-dom";
import type { MasterContactListItem } from "../../../api/client.ts";
import { getCountryFlag } from "../../../components/CountryName.tsx";
import IdentityCell from "../../../components/IdentityCell.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import UserAvatar from "../../../components/UserAvatar.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";

interface ContactsTableProps {
  contacts: MasterContactListItem[];
  /** Search or filters are narrowing the list (for the empty message). */
  filtered?: boolean;
}

export default function ContactsTable({ contacts, filtered = false }: ContactsTableProps) {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();

  return (
    <ScrollTable>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Birth year</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Email</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {contacts.map((c) => (
          <TableRow
            key={c.id}
            hover
            sx={{ cursor: "pointer" }}
            onClick={() => navigate(tenantPath(`/contacts/${c.id}`))}
          >
            <TableCell>
              <IdentityCell
                avatar={<UserAvatar displayName={c.full_name} size={28} tooltip={false} />}
                name={c.full_name}
              />
            </TableCell>
            <TableCell>{c.birth_year ?? "-"}</TableCell>
            <TableCell>
              {[c.city, c.country ? `${getCountryFlag(c.country)} ${c.country}` : null]
                .filter(Boolean)
                .join(", ") || "-"}
            </TableCell>
            <TableCell>
              {c.has_email ? (
                <Chip icon={<Mail fontSize="small" />} label="Email" size="small" variant="outlined" />
              ) : (
                "-"
              )}
            </TableCell>
          </TableRow>
        ))}
        {contacts.length === 0 && (
          <EmptyTableRow colSpan={4}>
            {filtered ? "No contacts match your filters" : "No contacts yet"}
          </EmptyTableRow>
        )}
      </TableBody>
    </ScrollTable>
  );
}
